/**
 * Browser Compatibility Utilities
 * Provides feature detection and fallbacks for cross-browser compatibility
 */

/**
 * Browser detection utilities
 */
export const BrowserDetection = {
  // User agent detection (use sparingly, prefer feature detection)
  isChrome: () => /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor) && !/Edg/.test(navigator.userAgent),
  isFirefox: () => /Firefox/.test(navigator.userAgent),
  isSafari: () => /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor) && !/Chrome/.test(navigator.userAgent),
  isEdge: () => /Edg/.test(navigator.userAgent),
  
  // Mobile detection
  isMobile: () => /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
  isIOS: () => /iPad|iPhone|iPod/.test(navigator.userAgent),
  isAndroid: () => /Android/.test(navigator.userAgent),
  
  // Version detection
  getChromeVersion: () => {
    const match = navigator.userAgent.match(/Chrome\/(\d+)/)
    return match ? parseInt(match[1]) : null
  },
  
  getFirefoxVersion: () => {
    const match = navigator.userAgent.match(/Firefox\/(\d+)/)
    return match ? parseInt(match[1]) : null
  },
  
  getSafariVersion: () => {
    const match = navigator.userAgent.match(/Version\/(\d+)/)
    return match ? parseInt(match[1]) : null
  }
}

/**
 * CSS Feature Detection
 */
export const CSSFeatures = {
  // CSS Grid support
  supportsGrid: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('display', 'grid')
    }
    // Fallback detection
    try {
      const testElement = document.createElement('div')
      testElement.style.display = 'grid'
      return testElement.style.display === 'grid'
    } catch (e) {
      return false
    }
  },
  
  // Flexbox support
  supportsFlex: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('display', 'flex')
    }
    try {
      const testElement = document.createElement('div')
      testElement.style.display = 'flex'
      return testElement.style.display === 'flex'
    } catch (e) {
      return false
    }
  },
  
  // CSS Custom Properties (CSS Variables)
  supportsCustomProperties: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('--test', '0')
    }
    return false
  },
  
  // Backdrop filter support
  supportsBackdropFilter: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('backdrop-filter', 'blur(1px)') || 
             CSS.supports('-webkit-backdrop-filter', 'blur(1px)')
    }
    return false
  },
  
  // CSS Transforms support
  supportsTransforms: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('transform', 'translateX(1px)')
    }
    const testElement = document.createElement('div')
    const transforms = ['transform', 'webkitTransform', 'mozTransform', 'msTransform']
    return transforms.some(prop => prop in testElement.style)
  },
  
  // CSS Transitions support
  supportsTransitions: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('transition', 'all 0.3s')
    }
    const testElement = document.createElement('div')
    const transitions = ['transition', 'webkitTransition', 'mozTransition', 'msTransition']
    return transitions.some(prop => prop in testElement.style)
  },
  
  // Viewport units support
  supportsViewportUnits: () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('width', '1vw')
    }
    return true // Most modern browsers support this
  }
}

/**
 * JavaScript Feature Detection
 */
export const JSFeatures = {
  // Intersection Observer API
  supportsIntersectionObserver: () => 'IntersectionObserver' in window,
  
  // ResizeObserver API
  supportsResizeObserver: () => 'ResizeObserver' in window,
  
  // matchMedia API
  supportsMatchMedia: () => 'matchMedia' in window,
  
  // requestAnimationFrame
  supportsRequestAnimationFrame: () => 'requestAnimationFrame' in window,
  
  // Local Storage
  supportsLocalStorage: () => {
    try {
      const test = 'test'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch (e) {
      return false
    }
  },
  
  // Fetch API
  supportsFetch: () => 'fetch' in window,
  
  // ES6 Features
  supportsES6: () => {
    try {
      // Test arrow functions, const/let, template literals
      eval('const test = () => `test`; test()')
      return true
    } catch (e) {
      return false
    }
  }
}

/**
 * Fallback implementations
 */
export const Fallbacks = {
  // matchMedia fallback
  matchMedia: (query) => {
    if (JSFeatures.supportsMatchMedia()) {
      return window.matchMedia(query)
    }
    
    // Simple fallback based on window dimensions
    const width = window.innerWidth
    const height = window.innerHeight
    
    // Parse common media queries
    if (query.includes('max-width')) {
      const maxWidth = parseInt(query.match(/max-width:\s*(\d+)px/)?.[1] || '0')
      return { matches: width <= maxWidth, media: query }
    }
    
    if (query.includes('min-width')) {
      const minWidth = parseInt(query.match(/min-width:\s*(\d+)px/)?.[1] || '0')
      return { matches: width >= minWidth, media: query }
    }
    
    // Default fallback
    return { matches: false, media: query }
  },
  
  // requestAnimationFrame fallback
  requestAnimationFrame: (callback) => {
    if (JSFeatures.supportsRequestAnimationFrame()) {
      return window.requestAnimationFrame(callback)
    }
    return setTimeout(callback, 16) // ~60fps
  },
  
  // IntersectionObserver fallback
  createIntersectionObserver: (callback, options = {}) => {
    if (JSFeatures.supportsIntersectionObserver()) {
      return new IntersectionObserver(callback, options)
    }
    
    // Simple fallback using scroll events
    const fallbackObserver = {
      observe: (element) => {
        const checkVisibility = () => {
          const rect = element.getBoundingClientRect()
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0
          
          if (isVisible) {
            callback([{ target: element, isIntersecting: true }])
          }
        }
        
        window.addEventListener('scroll', checkVisibility)
        window.addEventListener('resize', checkVisibility)
        checkVisibility() // Initial check
      },
      unobserve: () => {
        // Cleanup would be more complex in real implementation
      },
      disconnect: () => {
        // Cleanup would be more complex in real implementation
      }
    }
    
    return fallbackObserver
  }
}

/**
 * Browser compatibility initialization
 */
export const initBrowserCompatibility = () => {
  // Add browser classes to document
  const browserClasses = []
  
  if (BrowserDetection.isChrome()) browserClasses.push('browser-chrome')
  if (BrowserDetection.isFirefox()) browserClasses.push('browser-firefox')
  if (BrowserDetection.isSafari()) browserClasses.push('browser-safari')
  if (BrowserDetection.isEdge()) browserClasses.push('browser-edge')
  if (BrowserDetection.isMobile()) browserClasses.push('browser-mobile')
  if (BrowserDetection.isIOS()) browserClasses.push('browser-ios')
  if (BrowserDetection.isAndroid()) browserClasses.push('browser-android')
  
  // Add feature support classes
  if (CSSFeatures.supportsGrid()) browserClasses.push('supports-grid')
  if (CSSFeatures.supportsFlex()) browserClasses.push('supports-flex')
  if (CSSFeatures.supportsCustomProperties()) browserClasses.push('supports-css-vars')
  if (CSSFeatures.supportsBackdropFilter()) browserClasses.push('supports-backdrop-filter')
  if (CSSFeatures.supportsTransforms()) browserClasses.push('supports-transforms')
  if (CSSFeatures.supportsTransitions()) browserClasses.push('supports-transitions')
  
  // Add classes to document
  document.documentElement.classList.add(...browserClasses)
  
  return {
    browser: BrowserDetection,
    css: CSSFeatures,
    js: JSFeatures,
    fallbacks: Fallbacks
  }
}

/**
 * Get browser compatibility report
 */
export const getBrowserCompatibilityReport = () => {
  return {
    userAgent: navigator.userAgent,
    browser: {
      name: BrowserDetection.isChrome() ? 'Chrome' :
            BrowserDetection.isFirefox() ? 'Firefox' :
            BrowserDetection.isSafari() ? 'Safari' :
            BrowserDetection.isEdge() ? 'Edge' : 'Unknown',
      version: BrowserDetection.getChromeVersion() || 
               BrowserDetection.getFirefoxVersion() || 
               BrowserDetection.getSafariVersion() || 'Unknown',
      mobile: BrowserDetection.isMobile()
    },
    features: {
      css: {
        grid: CSSFeatures.supportsGrid(),
        flex: CSSFeatures.supportsFlex(),
        customProperties: CSSFeatures.supportsCustomProperties(),
        backdropFilter: CSSFeatures.supportsBackdropFilter(),
        transforms: CSSFeatures.supportsTransforms(),
        transitions: CSSFeatures.supportsTransitions(),
        viewportUnits: CSSFeatures.supportsViewportUnits()
      },
      javascript: {
        intersectionObserver: JSFeatures.supportsIntersectionObserver(),
        resizeObserver: JSFeatures.supportsResizeObserver(),
        matchMedia: JSFeatures.supportsMatchMedia(),
        requestAnimationFrame: JSFeatures.supportsRequestAnimationFrame(),
        localStorage: JSFeatures.supportsLocalStorage(),
        fetch: JSFeatures.supportsFetch(),
        es6: JSFeatures.supportsES6()
      }
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1
    }
  }
}

export default {
  BrowserDetection,
  CSSFeatures,
  JSFeatures,
  Fallbacks,
  initBrowserCompatibility,
  getBrowserCompatibilityReport
}